import { useQuery, UseQueryResult, useMutation } from '@tanstack/react-query';
import { API } from 'api';
import {
  OrganizationPurchase,
  OrganizationPurchaseData,
} from 'features/organizations/organizations.types';

type UseOrganizationPurchaseProps = {
  processOrganizationPurchase?: OrganizationPurchaseData;
};

type UseOrganizationPurchase = Omit<
  UseQueryResult<OrganizationPurchase, Error>,
  'data'
> & {
  previewPurchase?: OrganizationPurchase;
  processOrganizationPurchase: (
    organizationPurchaseData: OrganizationPurchaseData,
  ) => Promise<OrganizationPurchase>;
  sendOrganizationQuote: (
    organizationQuoteData: OrganizationQuoteData,
  ) => Promise<OrganizationPurchase>;
};

export type OrganizationQuoteData = {
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  proSeats?: number;
  regularSeats?: number;
  postalCode?: string;
  address?: string;
  additionalEmails?: string;
  poNumber?: string;
};

export type OrganizationQuoteDataServer = Omit<
  OrganizationQuoteData,
  'additionalEmails'
> & {
  additionalEmails?: string[];
};

const fetchOrganizationPurchasePreview = async (
  data: OrganizationPurchaseData,
): Promise<OrganizationPurchase> => {
  const response = await API.post('organization/purchase/preview/', data);
  return response.data;
};

const processPurchase = async (
  data: OrganizationPurchaseData,
): Promise<OrganizationPurchase> => {
  const response = await API.post('organization/purchase/', data);
  return response.data;
};

const sendQuote = async (
  data: OrganizationQuoteData,
): Promise<OrganizationPurchase> => {
  const emailsList = data.additionalEmails
    ? data.additionalEmails.split(',').map((email) => email.trim())
    : undefined;

  const transformedData: OrganizationQuoteDataServer = {
    ...data,
    additionalEmails: emailsList,
  };

  const response = await API.post<OrganizationPurchase>(
    'organization/purchase/quote/',
    transformedData,
  );
  return response.data;
};

export const useOrganizationPurchase = (
  props: UseOrganizationPurchaseProps,
): UseOrganizationPurchase => {
  const { processOrganizationPurchase: organizationPurchaseData } = props;

  const { data: previewPurchase, ...results } = useQuery<OrganizationPurchase>({
    queryKey: [
      'organizationPurchasePreview',
      {
        regularSeats: organizationPurchaseData?.regularSeats || 0,
        proSeats: organizationPurchaseData?.proSeats || 0,
      },
    ],
    queryFn: () => fetchOrganizationPurchasePreview(organizationPurchaseData!),
    staleTime: 60 * 60 * 1000,
    enabled:
      (organizationPurchaseData?.regularSeats || 0) +
        (organizationPurchaseData?.proSeats || 0) >
      0,
  });

  const processPurchaseMutation = useMutation<
    OrganizationPurchase,
    Error,
    OrganizationPurchaseData
  >({
    mutationFn: processPurchase,
  });

  const sendQuoteMutation = useMutation<
    OrganizationPurchase,
    Error,
    OrganizationQuoteData
  >({
    mutationFn: sendQuote,
  });

  const processOrganizationPurchase = async (
    data: OrganizationPurchaseData,
  ): Promise<OrganizationPurchase> => {
    const response = await processPurchaseMutation.mutateAsync(data);
    return response;
  };

  const sendOrganizationQuote = async (
    organizationQuoteData: OrganizationQuoteData,
  ): Promise<OrganizationPurchase> => {
    const response = await sendQuoteMutation.mutateAsync(organizationQuoteData);
    return response;
  };

  return {
    ...results,
    previewPurchase,
    processOrganizationPurchase,
    sendOrganizationQuote,
  };
};
